import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get, cloneDeep } from "lodash";
import * as Sentry from '@sentry/browser';

import ReportContainer from "../ReportContainer";
import ClassProgressContent from "./ClassProgressContent";
import classProgressActions from '../../state/classprogress/actions';
import { fetchActivitiesForClassProgressReport, fetchMasteriesForClassProgressReport, fetchStudentTrends, generateClassProgressReport } from "../../util/generators/generateClassProgressReport";
import { ASSESSMENT_TYPE_FILTERS, localesUnderscoreMap } from "../../../../constants/constants";
import { states } from '../../values/enums';
import { msbType } from "../../../../constants/constants";
import { scaleType } from "../../util/scaling";
import { logSentryException } from "../../../../services/util";
import { CLASSPROGRESS_REPORT_TYPES } from "../TeacherSelections/ClassProgressReportType";
import { LOCALE_OPTIONS } from "../../../../services/badging/constants";

export default function ClassProgress() {
  const dispatch = useDispatch();
  const setClassProgressReduxState = state => dispatch(classProgressActions.setState(state));
  const activities = useSelector(state => state.reports.classprogress.activities)
  const masteries = useSelector(state => state.reports.classprogress.masteries)
  const studentTrends = useSelector(state => state.reports.classprogress.studentTrends)
  const [status, setStatus] = useState(null);

  const roster = useSelector(state => state.reports.roster);
  const students = roster?.data?.student;
  const classId = roster?.ui?.selections?.classroom?.id;
  
  const teacherSelections = useSelector(state => state.reports.teacher);
  const metricId = get(teacherSelections, 'selections.metric.value');
  const selectedScale = get(teacherSelections, 'selections.scale');
  const selectedBenchmark = get(teacherSelections, 'selections.benchmark');
  const customBenchmarks = get(teacherSelections, 'customBenchmarks')
  const selectedLocale = get(teacherSelections, 'selectedLocale');
  const selectedStatePercentileCuts = get(teacherSelections, 'selectedStatePercentileCuts')
  const isBenchmarkOnly = get(teacherSelections, 'selectedAssessmentTypeFilter') === ASSESSMENT_TYPE_FILTERS.BENCHMARK_ONLY;
  const isThreadOverview = get(teacherSelections, 'classProgressReportType') === CLASSPROGRESS_REPORT_TYPES.THREAD_OVERVIEW;
  const [reportData, setReportData] = useState([]);

  let scaleId = selectedScale?.value;
  if (metricId === msbType.ORF) scaleId = scaleType.WCPM;

  useEffect(() => {
    if (!selectedLocale) return;
    fetchActivitiesAndMasteries();
  }, [selectedLocale, classId]);

  useEffect(() => {
    if (!activities[selectedLocale]) return;
    generateReport();
  }, [activities, metricId, customBenchmarks, selectedLocale]);

  const fetchActivitiesAndMasteries = async () => {
    setStatus(states.PENDING);
    try {
      const params = { students, selectedLocale, selectedStatePercentileCuts };
      const language = LOCALE_OPTIONS.find(i => i.value === localesUnderscoreMap[selectedLocale])?.label;
      const [fetchedActivities, fetchedMasteries, fetchedStudentTrends] = await Promise.all([fetchActivitiesForClassProgressReport(params), fetchMasteriesForClassProgressReport(params), fetchStudentTrends(students, language)]);
      const nextActivities = cloneDeep(activities);
      nextActivities[selectedLocale] = fetchedActivities;
      const nextMasteries = cloneDeep(masteries);
      nextMasteries[selectedLocale] = fetchedMasteries;
      const nextStudentTrends = cloneDeep(studentTrends);
      nextStudentTrends[selectedLocale] = fetchedStudentTrends;
      setClassProgressReduxState({activities: nextActivities, masteries: nextMasteries, studentTrends: nextStudentTrends});
      setStatus(states.COMPLETE);
    } catch (err) {
      console.error(err);
      logSentryException(new Error('Class progress report generation failure'), 'Error generateClassProgressReport', { originalError: err?.message, originalStack: err?.stack });
      setStatus(states.ERROR);
    }
  }

  const generateReport = async () => {
    const params = {
      activities,
      masteries,
      students,
      metricId,
      scaleId,
      selectedBenchmark,
      customBenchmarks,
      selectedLocale,
      isBenchmarkOnly,
      studentTrends
    };
    const classProgressReport = await generateClassProgressReport(params);
    setReportData(classProgressReport);
  }

  return (
    <ReportContainer>
      <ClassProgressContent
        status={status}
        reportData={reportData}
        isBenchmarkOnly={isBenchmarkOnly}
        scaleId={scaleId}
        isThreadOverview={isThreadOverview}
      />
    </ReportContainer>
  );
}
